import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const Support = () => {
  const [chats, setChats] = useState([]);
  const [groupedChats, setGroupedChats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [userTickets, setUserTickets] = useState([]);
  const navigate = useNavigate();
  
  const latestChatsRef = useRef([]);
  const messagesEndRef = useRef(null);

  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
  //const isLoggedIn = !!userInfo.token;

  useEffect(() => {
    loadUserTicketsFromStorage();
    fetchChats();
    
    const pollInterval = setInterval(() => {
      fetchChats();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  useEffect(() => {
    groupChatsByTicket();
  }, [chats]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load user tickets from localStorage
  const loadUserTicketsFromStorage = () => {
    const username = userInfo.username || 'guest';
    const storageKey = `userTickets_${username}`;
    const savedTickets = JSON.parse(localStorage.getItem(storageKey)) || [];
    setUserTickets(savedTickets);
  };

  // Save ticket to localStorage
  const saveTicketToStorage = (ticketNum) => {
    const username = userInfo.username || 'guest';
    const storageKey = `userTickets_${username}`;
    const existingTickets = JSON.parse(localStorage.getItem(storageKey)) || [];
    
    if (!existingTickets.includes(ticketNum)) {
      const updatedTickets = [...existingTickets, ticketNum];
      localStorage.setItem(storageKey, JSON.stringify(updatedTickets));
      setUserTickets(updatedTickets);
    }
  };

  // Group chats by ticket number
  const groupChatsByTicket = () => {
    const grouped = chats.reduce((acc, chat) => {
      const ticket = chat.ticketnumber || 'general';
      if (!acc[ticket]) {
        acc[ticket] = [];
      }
      acc[ticket].push(chat);
      return acc;
    }, {});
    
    // Sort messages within each ticket by date
    Object.keys(grouped).forEach(ticket => {
      grouped[ticket].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    
    setGroupedChats(grouped);
  };

  const fetchChats = async () => {
    const username = userInfo.username || 'guest'; // Use 'guest' as default username
    try {
      const response = await fetch(`/api/chats/${username}`);
      if (!response.ok) {
        throw new Error('Failed to fetch chats');
      }
      const data = await response.json();
      
      // Compare with previous chats to check for new messages
      const hasNewMessages = data.some(newChat => {
        const existingChat = latestChatsRef.current.find(chat => chat.id === newChat.id);
        return !existingChat || new Date(newChat.date) > new Date(existingChat.date);
      });

      if (hasNewMessages) {
        // Show notification for new messages
        toast.info('New message received!', {
          position: "top-right",
          autoClose: 3000,
        });
      }

      setChats(data);
      latestChatsRef.current = data;
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('attachments', file);
    });

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload files');
      }

      const data = await response.json();
      return data.filePaths;
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
      return [];
    }
  };

  // Function to get the maximum ticket number from existing chats
  const getMaxTicketNumber = () => {
    if (chats.length === 0) return 0;
    
    const ticketNumbers = chats
      .map(chat => parseInt(chat.ticketnumber))
      .filter(num => !isNaN(num));
    
    return ticketNumbers.length > 0 ? Math.max(...ticketNumbers) : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      let attachmentPaths = [];
      if (attachments.length > 0) {
        attachmentPaths = await handleFileUpload(attachments);
      }

      // Generate new ticket number by getting max ticket number and incrementing by 1
      let currentTicket;
      if (selectedTicket) {
        currentTicket = selectedTicket;
      } else if (ticketNumber) {
        currentTicket = ticketNumber;
      } else {
        // Create new ticket with max ticket number + 1
        const maxTicketNumber = getMaxTicketNumber();
        currentTicket = maxTicketNumber + 1;
      }
      
      setTicketNumber(currentTicket);
      
      // Save ticket to localStorage
      saveTicketToStorage(currentTicket);

      // Get user name - prioritize name, then username, then fallback to 'Guest'
      const userName = userInfo.name || userInfo.username || 'Guest';

      const response = await fetch('/api/chats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_id: userInfo.id || userInfo.username || 'guest',
          from_name: userName,
          toid: 'admin',
          to_name: 'Support Team',
          message_title: newTitle || 'New Support Request',
          message_body: newMessage,
          ticketnumber: currentTicket,
          category: 'support',
          state: 'unread',
          attachments: attachmentPaths
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setNewMessage('');
      setNewTitle('');
      setAttachments([]);
      fetchChats();
      
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid file type. Only images and PDFs are allowed.`);
        return false;
      }
      
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachment = (attachment, isUrl = false) => {
    if (!attachment) return null;
    
    const fileName = isUrl ? attachment.split('/').pop() : attachment.name;
    const fileType = isUrl ? 
      (attachment.toLowerCase().includes('.pdf') ? 'application/pdf' : 'image') :
      attachment.type;
    
    if (fileType.startsWith('image/') || fileType === 'image') {
      return (
        <div style={{ marginTop: '10px' }}>
          <img 
            src={isUrl ? `http://localhost:5000${attachment}` : URL.createObjectURL(attachment)}
            alt={fileName}
            style={{
              maxWidth: '200px',
              maxHeight: '200px',
              borderRadius: '8px',
              border: '1px solid #dce4ec',
              cursor: 'pointer'
            }}
            onClick={() => {
              const url = isUrl ? `http://localhost:5000${attachment}` : URL.createObjectURL(attachment);
              window.open(url, '_blank');
            }}
          />
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px' }}>
            {fileName}
          </div>
        </div>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <div style={{ 
          marginTop: '10px',
          padding: '10px',
          border: '1px solid #dce4ec',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer'
        }}
        onClick={() => {
          const url = isUrl ? `http://localhost:5000${attachment}` : URL.createObjectURL(attachment);
          window.open(url, '_blank');
        }}>
          <span style={{ fontSize: '24px' }}>📄</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>{fileName}</div>
            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>PDF Document</div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <a href="/"
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#2980b9';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#3498db';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <span style={{ fontSize: '18px' }}>←</span>
          Back to Home
        </a>
      </div>

      <h1 style={{
        color: '#2c3e50',
        textAlign: 'center',
        marginBottom: '30px',
        fontSize: '2.5rem',
        fontWeight: '300'
      }}>Customer Support</h1>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        {/* Tickets Sidebar */}
        <div style={{
          width: '300px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: '20px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>Your Tickets</h3>
          
          {/* New Ticket Button */}
          <button
            onClick={() => {
              setSelectedTicket(null);
              setTicketNumber(null);
              setNewTitle('');
              setNewMessage('');
              setAttachments([]);
            }}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: selectedTicket === null ? '#3498db' : '#ecf0f1',
              color: selectedTicket === null ? 'white' : '#2c3e50',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '15px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            + New Ticket
          </button>

          {/* Tickets List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {Object.keys(groupedChats).length === 0 ? (
              <div style={{ textAlign: 'center', color: '#7f8c8d', fontSize: '14px' }}>
                No tickets yet
              </div>
            ) : (
              Object.keys(groupedChats).map((ticketNum) => {
                const ticketMessages = groupedChats[ticketNum];
                const lastMessage = ticketMessages[ticketMessages.length - 1];
                const isSelected = selectedTicket === ticketNum;
                
                return (
                  <div
                    key={ticketNum}
                    onClick={() => {
                      setSelectedTicket(ticketNum);
                      setTicketNumber(parseInt(ticketNum));
                    }}
                    style={{
                      padding: '12px',
                      backgroundColor: isSelected ? '#e3f2fd' : '#f8f9fa',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      marginBottom: '10px',
                      border: isSelected ? '2px solid #2196f3' : '1px solid #dce4ec',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>
                      Ticket #{ticketNum}
                    </div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                      {lastMessage.message_title}
                    </div>
                    <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '4px' }}>
                      {formatDate(lastMessage.date)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#27ae60', marginTop: '2px' }}>
                      {ticketMessages.length} message{ticketMessages.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Container */}
        <div style={{
          flex: 1,
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {/* Chat Header */}
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #dce4ec',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>
              {selectedTicket ? `Ticket #${selectedTicket}` : 'New Support Request'}
            </h3>
          </div>

          {/* Chat Messages */}
          <div style={{
            padding: '20px',
            maxHeight: '400px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', color: '#7f8c8d' }}>Loading messages...</div>
            ) : error ? (
              <div style={{ textAlign: 'center', color: '#e74c3c' }}>{error}</div>
            ) : selectedTicket === null && chats.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#7f8c8d' }}>Start a new conversation!</div>
            ) : selectedTicket === null ? (
              <div style={{ textAlign: 'center', color: '#7f8c8d' }}>Select a ticket or create a new one</div>
            ) : (
              (groupedChats[selectedTicket] || []).map((message) => (
                <div key={message.id} style={{
                  padding: '15px',
                  backgroundColor: message.from_name === userInfo.name ? '#e3f2fd' : '#f8f9fa',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${message.from_name === userInfo.name ? '#2196f3' : '#27ae60'}`,
                  marginLeft: message.from_name === userInfo.name ? 'auto' : '0',
                  marginRight: message.from_name === userInfo.name ? '0' : 'auto',
                  maxWidth: '80%'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <strong style={{ color: '#2c3e50' }}>{message.message_title}</strong>
                    <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                      {formatDate(message.date)}
                    </span>
                  </div>
                  <div style={{ color: '#34495e', lineHeight: '1.5' }}>
                    {message.message_body}
                  </div>
                  
                  {/* Display attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                        Attachments:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {message.attachments.map((attachment, index) => (
                          <div key={index}>
                            {renderAttachment(attachment, true)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#95a5a6' }}>
                    From: {message.from_name} | Ticket #{message.ticketnumber}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        <form onSubmit={handleSubmit} style={{
          padding: '20px',
          borderTop: '1px solid #dce4ec',
          backgroundColor: '#f8f9fa'
        }}>
          {!ticketNumber && (
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Message subject..."
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #dce4ec',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '10px'
              }}
            />
          )}
          
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message here..."
            style={{
              width: '100%',
              height: '100px',
              padding: '12px',
              border: '1px solid #dce4ec',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical',
              marginBottom: '10px'
            }}
          />

          {/* File Upload Section */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{
              display: 'inline-block',
              padding: '8px 16px',
              backgroundColor: '#ffffff',
              border: '1px solid #dce4ec',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#495057'
            }}>
              <span style={{ marginRight: '8px' }}>📎</span>
              Attach Files
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleAttachmentChange}
                style={{ display: 'none' }}
              />
            </label>
            
            {/* Display selected attachments */}
            {attachments.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>
                  Selected Files:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {attachments.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      {renderAttachment(file)}
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            style={{
              padding: '12px 24px',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#219a52';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#27ae60';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Send Message
          </button>
        </form>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Support;
